import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminPayments,
  fetchPendingPayments,
  approvePayment,
  rejectPayment,
  selectAllPayments,
  selectTotalPayments,
  selectPaymentLoading,
  selectPaymentError,
  selectPagination,
  selectPendingPayments,
  selectPendingPaymentsCount,
  selectPendingLoading,
  selectTotalPaymentAmount,
  selectApprovedAmount,
  selectPendingAmount,
  setFilters,
  setPagination,
  clearFilters,
  clearError,
} from "../redux/slices/adminPaymentsSlice";

// MUI Imports
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Tooltip,
  LinearProgress,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const AdminPayments = () => {
  const dispatch = useDispatch();
  const payments = useSelector(selectAllPayments);
  const totalPayments = useSelector(selectTotalPayments);
  const loading = useSelector(selectPaymentLoading);
  const error = useSelector(selectPaymentError);
  const pagination = useSelector(selectPagination);
  const pendingPayments = useSelector(selectPendingPayments);
  const pendingPaymentsCount = useSelector(selectPendingPaymentsCount);
  const pendingLoading = useSelector(selectPendingLoading);
  const totalAmount = useSelector(selectTotalPaymentAmount);
  const approvedAmount = useSelector(selectApprovedAmount);
  const pendingAmount = useSelector(selectPendingAmount);

  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [dialogAction, setDialogAction] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch payments on mount and when filters/pagination change
  useEffect(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };

    if (statusFilter) params.status = statusFilter;
    if (searchTerm) params.search = searchTerm;

    dispatch(fetchAdminPayments(params));
  }, [dispatch, page, rowsPerPage, statusFilter, searchTerm]);

  // Fetch pending payments on mount
  useEffect(() => {
    dispatch(fetchPendingPayments());
  }, [dispatch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      dispatch(fetchPendingPayments());
    }
  };

  const handleRefresh = () => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (statusFilter) params.status = statusFilter;
    if (searchTerm) params.search = searchTerm;
    dispatch(fetchAdminPayments(params));
    dispatch(fetchPendingPayments());
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setSearchTerm("");
    setPage(0);
    dispatch(clearFilters());
    dispatch(fetchAdminPayments({ page: 1, limit: rowsPerPage }));
  };

  const handleApprove = (payment) => {
    setSelectedPayment(payment);
    setDialogAction("approve");
    setRejectionReason("");
    setOpenDialog(true);
  };

  const handleReject = (payment) => {
    setSelectedPayment(payment);
    setDialogAction("reject");
    setRejectionReason("");
    setOpenDialog(true);
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setOpenViewDialog(true);
  };

  const handleConfirmAction = async () => {
    if (dialogAction === "approve") {
      const result = await dispatch(approvePayment(selectedPayment._id));
      if (result.meta.requestStatus === "fulfilled") {
        setSnackbar({
          open: true,
          message: "Payment approved successfully!",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to approve payment",
          severity: "error",
        });
      }
    } else if (dialogAction === "reject") {
      // You can pass rejection reason if your API supports it
      const result = await dispatch(rejectPayment(selectedPayment._id));
      if (result.meta.requestStatus === "fulfilled") {
        setSnackbar({
          open: true,
          message: "Payment rejected successfully!",
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to reject payment",
          severity: "error",
        });
      }
    }
    setOpenDialog(false);
    setSelectedPayment(null);
    setRejectionReason("");
    // Refresh data
    handleRefresh();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPayment(null);
    setRejectionReason("");
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedPayment(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ✅ FIXED: Get amount from invoiceId
  // const getPaymentAmount = (payment) => {
  //   return payment.invoiceId?.amount || payment?.amount || 0;
  // };

  // ✅ FIXED: Get amount from invoiceId with proper null checks
const getPaymentAmount = (payment) => {
  if (!payment) return 0;
  return payment.invoiceId?.amount || payment.amount || 0;
};

  const getStatusChip = (status) => {
    const statusMap = {
      PENDING: { color: "warning", label: "Pending" },
      APPROVED: { color: "success", label: "Approved" },
      REJECTED: { color: "error", label: "Rejected" },
      VERIFIED: { color: "info", label: "Verified" },
    };
    const config = statusMap[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₹0";
    return `₹${amount}`;
  };

  const getDisplayData = () => {
    if (tabValue === 1) {
      return pendingPayments;
    }
    return payments;
  };

  const displayData = getDisplayData();
  const isLoading = tabValue === 1 ? pendingLoading : loading;

  if (isLoading && displayData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Payment Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Payments
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold">
                {totalPayments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold">
                {formatCurrency(totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Approved Amount
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold" color="success.main">
                {formatCurrency(approvedAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Amount
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
                {formatCurrency(pendingAmount)}
              </Typography>
              {pendingPaymentsCount > 0 && (
                <Chip
                  label={`${pendingPaymentsCount} pending`}
                  color="warning"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="All Payments" />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <span>Pending Payments</span>
                {pendingPaymentsCount > 0 && (
                  <Chip
                    label={pendingPaymentsCount}
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="VERIFIED">Verified</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search by UTR, restaurant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="body2" color="textSecondary" align="right">
              Total: <strong>{totalPayments}</strong> payments
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>UTR Number</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Restaurant</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Invoice</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.map((payment) => (
                <TableRow key={payment._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {payment.utrNumber || "N/A"}
                    </Typography>
                    {payment.rejectionReason && (
                      <Typography variant="caption" color="error" display="block">
                        Reason: {payment.rejectionReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.restaurantId?.restaurantName || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {payment.restaurantId?.ownerName || ""}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Status: {payment.restaurantId?.subscriptionStatus || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {payment.invoiceId ? (
                      <>
                        <Typography variant="body2">
                          {payment.invoiceId.invoiceNumber || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Due: {formatDate(payment.invoiceId.dueDate)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No Invoice
                      </Typography>
                    )}
                  </TableCell>
                  {/* ✅ FIXED: Show amount from invoiceId */}
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="primary">
                      {formatCurrency(getPaymentAmount(payment))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(payment.status)}
                    {payment.status === "PENDING" && (
                      <Box mt={0.5}>
                        <Chip
                          label="Awaiting Approval"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(payment.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      {payment.status === "PENDING" ? (
                        <>
                          <Tooltip title="Approve Payment">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleApprove(payment)}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject Payment">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleReject(payment)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() => handleViewDetails(payment)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="View Details">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleViewDetails(payment)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {displayData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <PaymentIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="textSecondary">
                      No payments found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {tabValue === 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalPayments}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count}`
            }
          />
        )}
        {tabValue === 1 && (
          <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" color="textSecondary">
              Total: <strong>{pendingPaymentsCount}</strong> pending payments
            </Typography>
            {pendingLoading && <LinearProgress sx={{ width: 100 }} />}
          </Box>
        )}
      </Paper>

      {/* Confirmation Dialog for Approve/Reject */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogAction === "approve" ? "Approve Payment" : "Reject Payment"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction === "approve"
              ? `Are you sure you want to approve this payment of ${formatCurrency(getPaymentAmount(selectedPayment))} from ${selectedPayment?.restaurantId?.restaurantName || "Unknown"}?`
              : `Are you sure you want to reject this payment of ${formatCurrency(getPaymentAmount(selectedPayment))} from ${selectedPayment?.restaurantId?.restaurantName || "Unknown"}?`}
          </DialogContentText>
          {dialogAction === "reject" && (
            <TextField
              autoFocus
              margin="dense"
              label="Rejection Reason"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter reason for rejection"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmAction}
            color={dialogAction === "approve" ? "success" : "error"}
            variant="contained"
          >
            {dialogAction === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Payment Details</Typography>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedPayment && (
            <Box sx={{ mt: 2 }}>
              {/* Payment Info */}
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Payment Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      UTR Number
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedPayment.utrNumber || "N/A"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Amount
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatCurrency(getPaymentAmount(selectedPayment))}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Status
                    </Typography>
                    <Box mt={0.5}>
                      {getStatusChip(selectedPayment.status)}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Payment Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedPayment.createdAt)}
                    </Typography>
                  </Paper>
                </Grid>
                {selectedPayment.rejectionReason && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, borderColor: "error.main" }}>
                      <Typography variant="caption" color="error">
                        Rejection Reason
                      </Typography>
                      <Typography variant="body1" color="error">
                        {selectedPayment.rejectionReason}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Restaurant Info */}
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Restaurant Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Restaurant Name
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayment.restaurantId?.restaurantName || "N/A"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Owner Name
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayment.restaurantId?.ownerName || "N/A"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Mobile
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayment.restaurantId?.mobile || "N/A"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      Subscription Status
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayment.restaurantId?.subscriptionStatus || "N/A"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Invoice Info */}
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Invoice Information
              </Typography>
              {selectedPayment.invoiceId ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Invoice Number
                      </Typography>
                      <Typography variant="body1">
                        {selectedPayment.invoiceId.invoiceNumber || "N/A"}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Invoice Amount
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {formatCurrency(selectedPayment.invoiceId.amount)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Due Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedPayment.invoiceId.dueDate)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Invoice Status
                      </Typography>
                      <Typography variant="body1">
                        {selectedPayment.invoiceId.status || "N/A"}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography color="textSecondary">No invoice associated</Typography>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPayments;